// review-remote-adapter.ts
//
// P7 §7 test 34-35, 38: `review-remote-v1` — controller thu MR + CI evidence
// (GitLab REST qua gitlab-merge-request-evidence-client.ts, hoặc `gh` cho
// host github.com — injected runner, KHÔNG gọi gh thật trong test), rồi
// worker viết review artifact trên evidence đó. Verdict PHẢI gắn đúng
// head/base SHA — nếu base đổi sau khi verdict được lập, verdict đó vô
// hiệu (test 35): diff đã review không còn là diff sắp đẩy (giống nguyên
// tắc review-state-store.ts's receiptOpensGate cho base_sha).
import { isGithubHost } from "../git-remote-host.js";
import {
  fetchGitlabMergeRequestEvidence,
  type GitlabEvidenceClientDeps,
  type GitlabEvidenceResult,
} from "./gitlab-merge-request-evidence-client.js";
import { gitlabCiStatus } from "./gitlab-pipeline-ci-status.js";
import { collectViaGh } from "./review-remote-gh-evidence.js";

export type GhRunner = (
  args: string[],
  cwd: string,
) => Promise<{ status: number | null; stdout: string; stderr: string }>;

export interface RemoteEvidence {
  source: "gitlab_rest" | "gh";
  headSha: string;
  baseSha: string;
  /**
   * GitLab: the newest MR pipeline's own status (success/failed/running/manual/…,
   * gitlab-pipeline-ci-status.ts). `gh`: "reported"/"unknown" — a check list, no
   * single computed status, so release-ci-gate.ts never reads it as a pass.
   */
  ciStatus: string;
  /** X2: SHA the latest MR pipeline ran on; undefined when the host reports none (checked by remote-evidence-candidate-binding.ts). */
  pipelineSha?: string;
  /** Web URL of the newest MR pipeline — named in the CI-gate messages. */
  pipelineUrl?: string;
  raw: unknown;
}

export type RemoteEvidenceResult =
  | { kind: "ok"; evidence: RemoteEvidence }
  | { kind: "resource_missing"; reason: string }
  | { kind: "not_found"; reason: string };

export interface CollectRemoteEvidenceInput {
  host: string;
  remoteUrl: string;
  sourceBranch: string;
  baseBranch: string;
  repoDir: string;
  expectedBaseSha: string;
  /** The commit the draft-MR push put on the branch (GitLab: which pipeline speaks for CI). */
  pushedSha?: string;
}

export interface CollectRemoteEvidenceDeps {
  gitlabClientDeps?: GitlabEvidenceClientDeps;
  ghRunner?: GhRunner;
}

function toRemoteEvidence(gitlabResult: GitlabEvidenceResult): RemoteEvidenceResult {
  if (gitlabResult.kind !== "ok") {
    return gitlabResult.kind === "not_found"
      ? { kind: "not_found", reason: gitlabResult.reason }
      : { kind: "resource_missing", reason: gitlabResult.reason };
  }
  const { mergeRequest, jobs, pipeline } = gitlabResult.evidence;
  const headSha = mergeRequest.sha ?? "";
  // Bug N (P18): `baseSha` is compared against the mission's real base oid by
  // checkVerdictStillBoundToCurrentBase (the check that gates G3), and the
  // GitHub path above fills it from `baseRefOid` — a SHA. This path used
  // `mergeRequest.target_branch`, a BRANCH NAME, so the comparison could only
  // ever fail and the release flow could only ever return
  // `review_evidence_base_mismatch`. G3 was unreachable on GitLab, which is
  // the host family this project actually uses. GitLab reports the real base
  // oid in `diff_refs.base_sha` (MergeRequestSchema is `.passthrough()`, so it
  // survives parsing); empty string when absent keeps the binding check
  // failing CLOSED rather than silently passing on a missing value.
  const diffRefs = (mergeRequest as { diff_refs?: { base_sha?: unknown } }).diff_refs;
  const baseSha = typeof diffRefs?.base_sha === "string" ? diffRefs.base_sha : "";
  return {
    kind: "ok",
    evidence: {
      source: "gitlab_rest",
      headSha,
      baseSha,
      // Dogfood 2026-10-01: the pipeline's own status, not a verdict re-derived
      // from the jobs (which read an `allow_failure` job as a red pipeline).
      ...gitlabCiStatus(pipeline, jobs),
      // The pipeline of the pushed commit with the highest id (not "the first
      // listed": gitlab-pipeline-ci-status.ts). A failed pipelines request
      // degrades to [] (arraySchemaOrEmpty) and reads as "no pipeline" — still
      // fail-closed for G3 (X2).
      pipelineSha: pipeline?.sha,
      raw: gitlabResult.evidence,
    },
  };
}

/**
 * Dispatch by host (test 38): github.com → `gh` (injected runner, never a
 * real call in tests); anything else → GitLab REST. Caller passes
 * `expectedBaseSha` so the returned verdict binding can be checked against
 * it by `bindReviewVerdictToShas` below.
 */
export async function collectRemoteEvidence(
  input: CollectRemoteEvidenceInput,
  deps: CollectRemoteEvidenceDeps = {},
): Promise<RemoteEvidenceResult> {
  if (isGithubHost(input.host)) {
    if (deps.ghRunner === undefined) {
      return {
        kind: "resource_missing",
        reason: "Host là github.com nhưng chưa injected ghRunner.",
      };
    }
    return collectViaGh(input, deps.ghRunner);
  }
  const gitlabResult = await fetchGitlabMergeRequestEvidence(
    {
      host: input.host,
      remoteUrl: input.remoteUrl,
      sourceBranch: input.sourceBranch,
      pushedSha: input.pushedSha,
    },
    deps.gitlabClientDeps,
  );
  return toRemoteEvidence(gitlabResult);
}

export interface ReviewVerdict {
  headSha: string;
  baseSha: string;
  risk: string;
  ciStatus: string;
}

export interface VerdictBindingCheck {
  ok: boolean;
  reason: string;
}

/**
 * Verdict chỉ mở gate khi base_sha vẫn khớp base HIỆN TẠI (test 35: base đổi
 * ⇒ verdict cũ vô hiệu, phải review lại) — cùng nguyên tắc
 * review-state-store.ts's receiptOpensGate.
 */
export function checkVerdictStillBoundToCurrentBase(
  verdict: ReviewVerdict,
  currentBaseSha: string,
): VerdictBindingCheck {
  if (verdict.baseSha !== currentBaseSha) {
    return {
      ok: false,
      reason:
        `Base đã đổi kể từ lúc lập verdict (verdict: ${verdict.baseSha}, hiện tại: ` +
        `${currentBaseSha}) ⇒ verdict vô hiệu, phải review-remote lại.`,
    };
  }
  return { ok: true, reason: "Verdict vẫn khớp base hiện tại." };
}
